import Layout from "@/components/feature/Layout";
import { alacaklarData } from "@/mocks/alacaklar";
import { siparislerData } from "@/mocks/siparisler";
import { useState, useMemo } from "react";

function formatCurrency(value: number, currency = "₺"): string {
  return `${value.toLocaleString("tr-TR")} ${currency}`;
}

const durumColors: Record<string, string> = {
  Bekliyor: "bg-gray-500/10 text-gray-400",
  Yaklaştı: "bg-gold-500/10 text-gold-400",
  Gecikti: "bg-red-500/10 text-red-400",
  Ödendi: "bg-emerald-500/10 text-emerald-400",
};

const sipDurumColors: Record<string, string> = {
  "Alındı": "bg-gray-500/10 text-gray-400",
  "Hazırlanıyor": "bg-gold-500/10 text-gold-400",
  "Atölyede": "bg-sky-500/10 text-sky-400",
  "Teslime Hazır": "bg-emerald-500/10 text-emerald-400",
  "Teslim Edildi": "bg-green-500/10 text-green-400",
  "İptal": "bg-red-500/10 text-red-400",
};

export default function Uyarilar() {
  const [activeTab, setActiveTab] = useState<"today" | "upcoming" | "overdue" | "todayOrder" | "upcomingOrder">("today");

  const todayStr = "2026-05-01";

  const todayPayments = useMemo(() => alacaklarData.filter((a) => a.odemeTarihi === todayStr && a.durum !== "Ödendi"), []);
  const upcomingPayments = useMemo(() => {
    return alacaklarData.filter((a) => {
      if (a.durum === "Ödendi") return false;
      const diff = Math.ceil((new Date(a.odemeTarihi).getTime() - new Date(todayStr).getTime()) / (1000 * 60 * 60 * 24));
      return diff > 0 && diff <= 3;
    });
  }, []);
  const overduePayments = useMemo(() => alacaklarData.filter((a) => a.durum === "Gecikti"), []);
  const todayOrders = useMemo(() => siparislerData.filter((s) => s.teslimTarihi === todayStr && s.durum !== "Teslim Edildi" && s.durum !== "İptal"), []);
  const upcomingOrders = useMemo(() => {
    return siparislerData.filter((s) => {
      if (s.durum === "Teslim Edildi" || s.durum === "İptal") return false;
      const diff = Math.ceil((new Date(s.teslimTarihi).getTime() - new Date(todayStr).getTime()) / (1000 * 60 * 60 * 24));
      return diff > 0 && diff <= 5;
    });
  }, []);

  const tabs = [
    { key: "today" as const, label: "Bugün Ödeme", count: todayPayments.length, icon: "ri-calendar-todo-line" },
    { key: "upcoming" as const, label: "3 Gün İçinde", count: upcomingPayments.length, icon: "ri-time-line" },
    { key: "overdue" as const, label: "Geciken", count: overduePayments.length, icon: "ri-error-warning-line" },
    { key: "todayOrder" as const, label: "Bugün Teslim", count: todayOrders.length, icon: "ri-gift-line" },
    { key: "upcomingOrder" as const, label: "Yaklaşan Sipariş", count: upcomingOrders.length, icon: "ri-truck-line" },
  ];

  const currentData =
    activeTab === "today"
      ? todayPayments
      : activeTab === "upcoming"
      ? upcomingPayments
      : activeTab === "overdue"
      ? overduePayments
      : activeTab === "todayOrder"
      ? todayOrders
      : upcomingOrders;

  const isOrderTab = activeTab === "todayOrder" || activeTab === "upcomingOrder";

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-100 mb-1">Akıllı Uyarı Paneli</h1>
        <p className="text-sm text-gray-500">Önemli tarihleri ve gecikmeleri takip edin, hızlı aksiyon alın.</p>
      </div>

      {/* Tab Bar */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
              activeTab === tab.key
                ? "bg-gold-500/10 text-gold-400 border border-gold-500/20"
                : "text-gray-400 hover:text-gray-200 bg-white/5 border border-transparent"
            }`}
          >
            <i className={tab.icon} />
            {tab.label}
            <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${tab.count > 0 ? "bg-red-500/20 text-red-400" : "bg-white/10 text-gray-500"}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                {isOrderTab ? (
                  <>
                    <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Müşteri</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Ürün</th>
                    <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Toplam</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Teslim Tarihi</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Durum</th>
                    <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">İşlemler</th>
                  </>
                ) : (
                  <>
                    <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Müşteri</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Telefon</th>
                    <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Kalan</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Son Tarih</th>
                    <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Durum</th>
                    <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">İşlemler</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {currentData.map((item: any) => (
                <tr key={item.id} className="table-row-hover border-b border-white/5 last:border-0">
                  {isOrderTab ? (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-200 font-medium">{item.musteriAdi}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">{item.urunAciklama}</td>
                      <td className="px-4 py-3 text-sm text-gray-200 text-right font-medium">{formatCurrency(item.toplamTutar)}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">{item.teslimTarihi}</td>
                      <td className="px-4 py-3">
                        <span className={`status-badge ${sipDurumColors[item.durum] || ""}`}>{item.durum}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`https://wa.me/${item.telefon.replace(/\s/g, "")}?text=Merhaba%20${encodeURIComponent(item.musteriAdi)},%20${encodeURIComponent(item.urunAciklama)}%20siparişiniz%20${encodeURIComponent(item.durum.toLowerCase())}%20durumdadır.%20Detaylar%20için%20bizimle%20iletişime%20geçebilirsiniz.`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors cursor-pointer"
                            title="WhatsApp"
                          >
                            <i className="ri-whatsapp-line" />
                          </a>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-200 font-medium">{item.adSoyad}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">{item.telefon}</td>
                      <td className="px-4 py-3 text-sm text-red-400 text-right font-medium">{formatCurrency(item.kalanTutar)}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">{item.odemeTarihi}</td>
                      <td className="px-4 py-3">
                        <span className={`status-badge ${durumColors[item.durum] || ""}`}>{item.durum}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`https://wa.me/${item.telefon.replace(/\s/g, "")}?text=Merhaba%20${encodeURIComponent(item.adSoyad)},%20kuyum%20alışverişinizle%20ilgili%20kalan%20ödemeniz%20${formatCurrency(item.kalanTutar)}%20olarak%20görünmektedir.%20Müsait%20olduğunuzda%20bizimle%20iletişime%20geçmenizi%20rica%20ederiz.`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors cursor-pointer"
                            title="WhatsApp"
                          >
                            <i className="ri-whatsapp-line" />
                          </a>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {currentData.length === 0 && (
          <div className="p-8 text-center text-gray-500 text-sm">Bu kategoride kayıt bulunmuyor.</div>
        )}
      </div>
    </Layout>
  );
}
